import { deleteRequest, getRequest, postRequest, putRequest } from '.'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import type CharacterRole from 'app/frontend/types/custom/CharacterRole'
import EventParticipantStatus from 'app/frontend/types/custom/EventParticipantStatus'
import type { EventPlanOverridesPatch } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/eventActivityPlanOverridesPatch'

export type EventActivitiesPlanningInfo = {
  activities: Record<
    string,
    {
      recommended_setup?: RecommendedSetup | null
      participant_kill_counts: Record<number, number>
      wishlist_items: Record<number, EventPlanningWishlistItem[]>
    }
  >
}

export type EventActivityPreviousSetup = {
  event: wowaudit.EventReference
  activityId: number
  totalParticipants: number
}

export type EventImportCandidate = {
  event: wowaudit.EventReference
  activities: {
    id: number
    encounterName?: string
    instanceName?: string
    thumbnailUrl?: string
    selectedCount: number
  }[]
}

export type EventChangelogEntry = {
  changedAt: string
  type: string
  change: {
    from: unknown
    to: unknown
    subject?: string | null
  }
  author: string | null
}

export type UpdateEventActivityPayload = Partial<
  Pick<
    wowaudit.EventActivity,
    | 'active'
    | 'order'
    | 'difficulty'
    | 'planOverrides'
    | 'notes'
    | 'linkedPlans'
  >
> & {
  planOverridesPatch?: EventPlanOverridesPatch
}

export type CreateEventActivitiesPayload = {
  activityType: wowaudit.EventActivity['activityType']
  addAt: 'beginning' | 'end'
  instanceId?: number
  difficulty?: wowaudit.EventActivity['difficulty']
  mode?:
    | 'show_instance_as_single_activity'
    | 'specific_encounter'
    | 'add_encounters_separately'
  encounterId?: number
  allowDuplicates?: boolean
}

type EventPlanningWishlistItem = {
  score?: {
    percentage?: number | string
    score?: number | string
    manual?: WishLegacyPriority[]
  } | null
  id: number
  icon: string
  name: string
  alternative?: unknown
  quality: string
  difficulty: string
  edited: boolean
  age?: number | null
}

export type RoleSetup = {
  recommendation: number
  distribution: { amount: number; fraction: number }[]
}

export type RecommendedSetup = {
  tanks: RoleSetup
  healers: RoleSetup
  melee: RoleSetup
  ranged: RoleSetup
}

export type TeamOverviewEventsOptions = {
  cursor?: number
  dir?: 'before' | 'after'
  limit?: number
}

export type TeamCalendarEventsOptions = {
  month: string
}

export type TeamListEventsOptions = {
  page: number
  tab?: 'upcoming' | 'past'
}

export type TeamOverviewEventsResponse = {
  events: wowaudit.EventReference[]
  nextCursor?: number
  prevCursor?: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export type TeamListEventsResponse = {
  events: wowaudit.EventReference[]
  page: number
  totalPages: number
  totalCount: number
}

export type BulkUpdateUserEventStatusesPayload = {
  status: EventParticipantStatus
  eventIds?: number[]
  teamId?: number | null
  fromDate?: string
  untilDate?: string
  weekDay?: number | null
  difficulty?: string | null
  optional?: boolean | null
  characterId?: number | null
  role?: CharacterRole | null
  comment?: string | null
  autoSignupOnly?: boolean
}

export type TeamEventRaidReportsMapResponse = {
  raidReportsByEventId: Record<string, number[]>
}

export const getEventActivityPlanningKey = (
  activity: Pick<wowaudit.EventActivity, 'difficulty' | 'encounter'>,
) => {
  return `${activity.encounter?.id || 0}-${activity.difficulty}`
}

export const getTeamEvents = async (
  id: number,
): Promise<wowaudit.EventReference[]> => {
  return await getRequest(`/api/teams/${id}/events`)
}

export const getTeamOverviewEvents = async (
  id: number,
  options?: TeamOverviewEventsOptions,
): Promise<TeamOverviewEventsResponse> => {
  const searchParams = new URLSearchParams()
  searchParams.set('overview', 'true')
  if (options?.cursor) {
    searchParams.set('cursor', options.cursor.toString())
  }
  if (options?.dir) {
    searchParams.set('dir', options.dir)
  }
  if (options?.limit) {
    searchParams.set('limit', options.limit.toString())
  }
  return await getRequest(`/api/teams/${id}/events?${searchParams.toString()}`)
}

export const getTeamCalendarEvents = async (
  id: number,
  options: TeamCalendarEventsOptions,
): Promise<wowaudit.EventReference[]> => {
  const searchParams = new URLSearchParams()
  searchParams.set('calendar', 'true')
  searchParams.set('month', options.month)
  return await getRequest(`/api/teams/${id}/events?${searchParams.toString()}`)
}

export const getTeamListEvents = async (
  id: number,
  options: TeamListEventsOptions,
): Promise<TeamListEventsResponse> => {
  const searchParams = new URLSearchParams()
  searchParams.set('list', 'true')
  searchParams.set('page', options.page.toString())
  if (options.tab) searchParams.set('tab', options.tab)
  return await getRequest(`/api/teams/${id}/events?${searchParams.toString()}`)
}

export const getTeamEventRaidReportsMap = async (
  teamId: number,
): Promise<TeamEventRaidReportsMapResponse> => {
  return await getRequest(`/api/teams/${teamId}/events/raid_reports_map`)
}

export const getUserListEvents = async (
  options: TeamListEventsOptions,
): Promise<TeamListEventsResponse> => {
  const searchParams = new URLSearchParams()
  searchParams.set('page', options.page.toString())
  if (options.tab) searchParams.set('tab', options.tab)
  return await getRequest(`/api/user/events?${searchParams.toString()}`)
}

export const bulkUpdateUserEventStatuses = async (
  payloadOrEventIds: BulkUpdateUserEventStatusesPayload | number[],
  status?: wowaudit.EventParticipantReference['status'],
): Promise<{ job_id: string }> => {
  const payload = Array.isArray(payloadOrEventIds)
    ? {
        eventIds: payloadOrEventIds,
        status: status as wowaudit.EventParticipantReference['status'],
      }
    : payloadOrEventIds

  return await putRequest('/api/user/events/bulk_update', {
    event: payload,
  })
}

export const getTeamEvent = async (
  teamId: number,
  eventId: number,
): Promise<wowaudit.Event> => {
  return await getRequest(`/api/teams/${teamId}/events/${eventId}`)
}

export const getEventRaidReports = async (
  teamId: number,
  eventId: number,
): Promise<wowaudit.RaidReport[]> => {
  return await getRequest(`/api/teams/${teamId}/events/${eventId}/raid_reports`)
}

export const deleteEventRaidReport = async (
  teamId: number,
  eventId: number,
  reportCode: string,
): Promise<void> => {
  await deleteRequest(
    `/api/teams/${teamId}/events/${eventId}/raid_reports/${reportCode}`,
  )
}

export const syncEventRaidReports = async (
  teamId: number,
  eventId: number,
): Promise<{ job_id: string }> => {
  return await postRequest(
    `/api/teams/${teamId}/events/${eventId}/raid_reports/sync`,
    {},
  )
}

export const linkEventRaidReport = async (
  teamId: number,
  eventId: number,
  raidReport: { reportUrl: string },
): Promise<{ job_id: string }> => {
  return await postRequest(
    `/api/teams/${teamId}/events/${eventId}/raid_reports/link`,
    {
      raidReport,
    },
  )
}

export const refreshEventRaidReport = async (
  teamId: number,
  eventId: number,
  reportCode: string,
): Promise<{ job_id: string }> => {
  return await postRequest(
    `/api/teams/${teamId}/events/${eventId}/raid_reports/${reportCode}/refresh`,
    {},
  )
}

export const getTeamEventChangelog = async (
  teamId: number,
  eventId: number,
): Promise<{ changelog: EventChangelogEntry[] }> => {
  return await getRequest(`/api/teams/${teamId}/events/${eventId}/changelog`)
}

export const updateTeamEvent = async (
  teamId: number,
  eventId: number,
  event: Partial<wowaudit.Event>,
): Promise<wowaudit.Event> => {
  return await putRequest(`/api/teams/${teamId}/events/${eventId}`, {
    event,
  })
}

export const updateEventParticipantStatus = async (
  teamId: number,
  eventParticipantId: number,
  status: wowaudit.EventParticipantReference['status'],
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      eventParticipant: { status },
    },
  )
}

export const updateEventParticipantSignup = async (
  teamId: number,
  eventParticipantId: number,
  signup: {
    status: wowaudit.EventParticipantReference['status']
    comment?: string
    characterId?: number
    role?: string
    specId?: number | null
  },
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      eventParticipant: signup,
    },
  )
}

export const updateEventParticipantAdminComment = async (
  teamId: number,
  eventParticipantId: number,
  adminComment: string,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      eventParticipant: { adminComment },
    },
  )
}

export const createEventParticipant = async (
  teamId: number,
  eventParticipant: {
    eventId: number
    characterId: number
    addedAsGuest: boolean
    role?: string
    specId?: number
    status?: wowaudit.EventParticipantReference['status']
    comment?: string
  },
): Promise<wowaudit.Event> => {
  return await postRequest(`/api/teams/${teamId}/event_participants`, {
    eventParticipant,
  })
}

export const createViewerEventParticipant = async (
  teamId: number,
  eventParticipant: {
    eventId: number
    characterId: number
    role: string
    status: wowaudit.EventParticipantReference['status']
    comment?: string
  },
): Promise<wowaudit.Event> => {
  return await createEventParticipant(teamId, {
    ...eventParticipant,
    addedAsGuest: true,
  })
}

export const deleteEventParticipant = async (
  teamId: number,
  eventParticipantId: number,
): Promise<wowaudit.Event> => {
  return await deleteRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
  )
}

export const updateEventParticipantSelection = async (
  teamId: number,
  eventParticipantId: number,
  selection: {
    activityId?: number
    selected?: boolean
    role?: string
    specId?: number | null
    characterId?: number
  },
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      eventParticipant: {
        selection,
      },
    },
  )
}

export const bulkUpdateEventActivitySelection = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
  selected: boolean,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/bulk_selection`,
    {
      eventActivity: { selected },
    },
  )
}

export const updateEventActivity = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
  eventActivity: UpdateEventActivityPayload,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}`,
    {
      event_activity: eventActivity,
    },
  )
}

export const bulkUpdateEventActivitiesActive = async (
  teamId: number,
  eventId: number,
  active: boolean,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/bulk_update_active`,
    { event_activity: { active } },
  )
}

export const exportEventActivityOverrides = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
  raidPlanId: number,
): Promise<wowaudit.Event> => {
  return await postRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/export_overrides`,
    { raid_plan_id: raidPlanId },
  )
}

export const createEventActivities = async (
  teamId: number,
  eventId: number,
  eventActivity: CreateEventActivitiesPayload,
): Promise<wowaudit.Event> => {
  return await postRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities`,
    {
      eventActivity,
    },
  )
}

export const deleteEventActivity = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
): Promise<wowaudit.Event> => {
  return await deleteRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}`,
  )
}

export const copyEventActivitySelections = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
  sourceActivityId: number,
  sourceEventId?: number,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/copy_selections`,
    {
      eventActivity: {
        sourceActivityId,
        sourceEventId,
      },
    },
  )
}

export const importEventActivityFromDefaultSetup = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/import_default_setup`,
    {},
  )
}

export const getEventActivityPreviousSetups = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
): Promise<{
  previousSetups: EventActivityPreviousSetup[]
}> => {
  return await getRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/previous_setups`,
  )
}

export const updateEventActivityOrder = async (
  teamId: number,
  eventId: number,
  activityOrder: number[],
): Promise<wowaudit.Event> => {
  return await putRequest(`/api/teams/${teamId}/events/${eventId}`, {
    event: { activityOrder },
  })
}

export const getEventImportCandidates = async (
  teamId: number,
  eventId: number,
): Promise<{ importCandidates: EventImportCandidate[] }> => {
  return await getRequest(
    `/api/teams/${teamId}/events/${eventId}/import_candidates`,
  )
}

export const importEventFromPreviousEvent = async (
  teamId: number,
  eventId: number,
  sourceEventId: number,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/import_from_event`,
    {
      event: { sourceEventId },
    },
  )
}

export const resetEventSelections = async (
  teamId: number,
  eventId: number,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/reset_selections`,
    {},
  )
}

export const bulkUpdateEventSelectionForAllActivities = async (
  teamId: number,
  eventId: number,
  selected: boolean,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/bulk_activity_selection`,
    { selected },
  )
}

export const getEventActivitiesPlanningInfo = async (
  teamId: number,
  eventId: number,
): Promise<EventActivitiesPlanningInfo> => {
  return await getRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/planning_info`,
  )
}
